import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as DashboardIcon } from 'src/Images/icons/sidebar/DashboardIcon.svg';
import { ReactComponent as EmptyDashboardIcon } from 'src/Images/icons/sidebar/EmptyDashboardIcon.svg';
import { ReactComponent as StationIcon } from 'src/Images/icons/sidebar/StationIcon.svg';
import { ReactComponent as EmptyStationIcon } from 'src/Images/icons/sidebar/EmptyStationIcon.svg';
import { ReactComponent as VehicleIcon } from 'src/Images/icons/sidebar/VehicleIcon.svg';
import { ReactComponent as EmptyVehicleIcon } from 'src/Images/icons/sidebar/EmptyVehicleIcon.svg';
import { ReactComponent as ReportWithTimerIcon } from 'src/Images/icons/sidebar/ReportWithTimer.svg';
import { ReactComponent as EmptyReportWithTimerIcon } from 'src/Images/icons/sidebar/EmptyReportWithTimerIcon.svg';
import { ReactComponent as ShortageReportIcon } from 'src/Images/icons/sidebar/ShortageReportIcon.svg';
import { ReactComponent as EmptyShortageReportIcon } from 'src/Images/icons/sidebar/EmptyShortageReportIcon.svg';
import { ReactComponent as InwardReportIcon } from 'src/Images/icons/sidebar/InwardReportIcon.svg';
import { ReactComponent as EmptyInwardReportIcon } from 'src/Images/icons/sidebar/EmptyInwardReportIcon.svg';
import { ReactComponent as OverviewVehicleTrackingIcon } from 'src/Images/icons/sidebar/OverviewVehicleTrackingIcon.svg';
import { ReactComponent as ClickedOverviewVehicleTrackingIcon } from 'src/Images/icons/sidebar/ClickedOverviewVehicleTrackingIcon.svg';
import { ReactComponent as GRNList } from 'src/Images/icons/sidebar/GRNList.svg';
import { ReactComponent as EmptyGRNList } from 'src/Images/icons/sidebar/EmptyGRNList.svg';
import { ReactComponent as StationActivityIcon } from 'src/Images/icons/sidebar/StationActivityIcon.svg';
import { ReactComponent as StationActivityLogInactiveIcon } from 'src/Images/icons/sidebar/StationActivityLogInactiveIcon.svg';
import { ReactComponent as OutwardIcon } from 'src/Images/icons/sidebar/outwardIcon.svg';
import { ReactComponent as OutwardInActiveIcon } from 'src/Images/icons/sidebar/outwardInActiveIcon.svg';
import { ReactComponent as VehicleTrackingListActiveIcon } from 'src/Images/icons/sidebar/VehicleTrackingListActiveIcon.svg';
import { ReactComponent as VehicleTrackingListIcon } from 'src/Images/icons/sidebar/VehicleTrackingListIcon.svg';
import { ReactComponent as OutwardReportIcon } from 'src/Images/icons/sidebar/OutwardReportIcon.svg';
import { ReactComponent as EmptyOutwardReportIcon } from 'src/Images/icons/sidebar/EmptyOutwardReportIcon.svg';
import { ReactComponent as OutwardReportTimeIcon } from 'src/Images/icons/sidebar/OutwardReportTimeIcon.svg';
import { ACTIONS } from 'src/utils/roles/actions';
import { usePermissions } from 'src/utils/hooks/usePermissions';
import { ReactComponent as InwardInsights } from 'src/Images/icons/sidebar/InwardInsights.svg';
import { ReactComponent as InwardReportSelectedIcon } from 'src/Images/icons/sidebar/InwardReportSelectedIcon.svg';
import { ReactComponent as OutwardInsights } from 'src/Images/icons/sidebar/OutwardInsights.svg';
import { ReactComponent as OutwardReportSelectedIcon } from 'src/Images/icons/sidebar/OutwardReportSelectedIcon.svg';

const SidebarData = () => {
  const AuthReducer = useSelector((state) => state.AuthReducer);
  const {
    superAdmin,
    organisationAdmin,
    normalUser,
    assignedPermissionsForCurrentUser,
  } = AuthReducer;

  // Use centralized permission hook
  const {
    hasPermission,
    positionPermissions,
    teamPermissions,
    isPermissionsLoaded,
  } = usePermissions();

  const noPermissionsAssigned =
    isPermissionsLoaded &&
    (positionPermissions?.length === 0 || !positionPermissions) &&
    (teamPermissions?.length === 0 || !teamPermissions);

  const hasInwardPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.INWARD.CREATE,
  );
  const hasOutwardPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CREATE,
  );
  const hasCreateVehicleTrackingPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.ADD,
  );

  const Sidebar = [
    {
      id: 1,
      name: 'Dashboard',
      url: 'dashboard/',
      inactiveIcon: <DashboardIcon className="icon-size-xxl" />,
      activeIcon: <EmptyDashboardIcon className="icon-size-xxl" />,
      showInSecondarySidebar: false,
    },
    {
      id: 2,
      name: 'Create Vehicle Tracking',
      url:
        !hasInwardPermission && hasOutwardPermission
          ? 'outward/create'
          : 'vehicle-tracking/create',
      inactiveIcon: <VehicleIcon className="icon-size-xxl" />,
      activeIcon: <EmptyVehicleIcon className="icon-size-xxl" />,
      showInSecondarySidebar: false,
    },
    {
      id: 3,
      name: 'Station Dashboard',
      url: `stations/`,
      inactiveIcon: <StationIcon className="icon-size-xxl" />,
      activeIcon: <EmptyStationIcon className="icon-size-xxl" />,
      showInSecondarySidebar: false,
    },
    {
      id: 4,
      name: 'Vehicle Tracking Planning',
      url: 'outward/planning/',
      matchUrls: ['/outward/planning/'],
      inactiveIcon: <OutwardIcon className="icon-size-xxl" />,
      activeIcon: <OutwardInActiveIcon className="icon-size-xxl" />,
      showInSecondarySidebar: false,
      action: ACTIONS.VEHICLE_PLANNING.VIEW,
    },
    {
      id: 5,
      name: 'Vehicle Tracking List',
      url: 'vehicle-tracking-list/',
      matchUrls: ['/vehicle-tracking-list/'],
      inactiveIcon: <VehicleTrackingListIcon className="icon-size-xxl" />,
      activeIcon: <VehicleTrackingListActiveIcon className="icon-size-xxl" />,
      showInSecondarySidebar: false,
    },
    {
      id: 6,
      name: 'Inward Insights',
      url: '#',
      inactiveIcon: <InwardInsights className="icon-size-xxl" />,
      activeIcon: <InwardReportSelectedIcon className="icon-size-xxl" />,
      showInSecondarySidebar: false,
      matchUrls: [
        '/report/vehicle-trackings/',
        '/report/variance/',
        '/report/inward/',
        '/report/inward/',
        '/report/inward-with-exit-time/',
        '/grn/list/',
        '/station-activity-logs/',
      ],

      groupChild: [
        {
          id: 7,
          name: 'Vehicle Tracking Report',
          url: '/report/vehicle-trackings/',
          matchUrls: ['/report/vehicle-trackings/'],
          inactiveIcon: (
            <OverviewVehicleTrackingIcon className="icon-size-xxl" />
          ),
          activeIcon: (
            <ClickedOverviewVehicleTrackingIcon className="icon-size-xxl" />
          ),
          action: ACTIONS.REPORT.VEHICLE_TRACKING,
        },
        {
          id: 8,
          name: 'Excess Shortage Report',
          url: '/report/variance/',
          matchUrls: ['/report/variance/'],
          inactiveIcon: <ShortageReportIcon className="icon-size-xxl" />,
          activeIcon: <EmptyShortageReportIcon className="icon-size-xxl" />,
          action: ACTIONS.REPORT.VARIANCE,
        },
        {
          id: 9,
          name: 'Inward Report',
          url: '/report/inward/',
          matchUrls: ['/report/inward/'],
          inactiveIcon: <InwardReportIcon className="icon-size-xxl" />,
          activeIcon: <EmptyInwardReportIcon className="icon-size-xxl" />,
          action: ACTIONS.REPORT.INWARD,
        },
        {
          id: 10,
          name: 'Inward report with exit time',
          url: '/report/inward-with-exit-time/',
          matchUrls: ['/report/inward-with-exit-time/'],
          inactiveIcon: <ReportWithTimerIcon className="icon-size-xxl" />,
          activeIcon: <EmptyReportWithTimerIcon className="icon-size-xxl" />,
          action: ACTIONS.REPORT.INWARD,
        },
        {
          id: 11,
          name: 'GRN List',
          url: '/grn/list/',
          matchUrls: ['/grn/list/'],
          inactiveIcon: <GRNList className="icon-size-xxl" />,
          activeIcon: <EmptyGRNList className="icon-size-xxl" />,
          action: ACTIONS.REPORT.GRN,
        },
      ],
    },
    {
      id: 12,
      name: 'Outward Insights',
      url: '#',
      inactiveIcon: <OutwardInsights className="icon-size-xxl" />,
      activeIcon: <OutwardReportSelectedIcon className="icon-size-xxl" />,
      showInSecondarySidebar: false,
      matchUrls: ['/report/outward/', '/report/outward-with-exit-time/'],

      groupChild: [
        {
          id: 13,
          name: 'Outward Report',
          url: '/report/outward/',
          matchUrls: ['/report/outward/'],
          inactiveIcon: <OutwardReportIcon className="icon-size-xxl" />,
          activeIcon: <EmptyOutwardReportIcon className="icon-size-xxl" />,
          action: ACTIONS.REPORT.OUTWARD,
        },
        {
          id: 14,
          name: 'Outward Report With Exit Time',
          url: '/report/outward-with-exit-time/',
          matchUrls: ['/report/outward-with-exit-time/'],
          inactiveIcon: <OutwardReportTimeIcon className="icon-size-xxl" />,
          activeIcon: <EmptyReportWithTimerIcon className="icon-size-xxl" />,
          action: ACTIONS.REPORT.OUTWARD,
        },
        {
          id: 17,
          name: 'Outward Variance Report',
          url: '/report/variance/outward/',
          matchUrls: ['/report/variance/outward/'],
          inactiveIcon: <InwardReportIcon className="icon-size-xxl" />,
          activeIcon: <EmptyInwardReportIcon className="icon-size-xxl" />,
          action: ACTIONS.REPORT.OUTWARD,
        },
      ],
    },
    {
      id: 15,
      name: 'Station Activity Log',
      url: '/station-activity-logs/',
      matchUrls: ['/station-activity-logs/'],
      inactiveIcon: <StationActivityIcon className="icon-size-xxl" />,
      activeIcon: <StationActivityLogInactiveIcon className="icon-size-xxl" />,
      showInSecondarySidebar: true,
    },
    {
      id: 16,
      name: 'Vehicle List',
      url: 'vehicle-list/',
      matchUrls: ['/vehicle-list/'],
      inactiveIcon: <OutwardIcon className="icon-size-xxl" />,
      activeIcon: <OutwardInActiveIcon className="icon-size-xxl" />,
      showInSecondarySidebar: true,
    },
    {
      id: 17,
      name: 'Transporter List',
      url: 'transporters/',
      matchUrls: ['/transporters/'],
      inactiveIcon: <OutwardIcon className="icon-size-xxl" />,
      activeIcon: <OutwardInActiveIcon className="icon-size-xxl" />,
      showInSecondarySidebar: true,
    },
  ];

  const [filteredSidebarData, setFilteredSidebarData] = useState([]);

  useEffect(() => {
    const filteredData = filterSidebarData();
    setFilteredSidebarData(filteredData);
  }, [superAdmin, organisationAdmin, assignedPermissionsForCurrentUser]);

  const filterSidebarData = () => {
    let roleFiltered;

    if (superAdmin === true) {
      roleFiltered = Sidebar.filter(
        (item) =>
          item.id === 1 ||
          item.id === 3 ||
          item.id === 4 ||
          item.id === 5 ||
          item.id === 6 ||
          item.id === 12 ||
          item.id === 15 ||
          item.id === 16 ||
          item.id === 17 ||
          (item.id === 2 &&
            (noPermissionsAssigned || hasCreateVehicleTrackingPermission)),
      );
    } else if (organisationAdmin === true) {
      roleFiltered = Sidebar.filter(
        (item) =>
          item.id === 1 ||
          item.id === 3 ||
          item.id === 4 ||
          item.id === 5 ||
          item.id === 6 ||
          item.id === 12 ||
          item.id === 15 ||
          item.id === 16 ||
          item.id === 17 ||
          (item.id === 2 &&
            (noPermissionsAssigned || hasCreateVehicleTrackingPermission)),
      );
    } else if (normalUser === true) {
      roleFiltered = Sidebar.filter(
        (item) =>
          item.id === 1 ||
          item.id === 3 ||
          item.id === 4 ||
          item.id === 5 ||
          item.id === 16 ||
          item.id === 17 ||
          (item.id === 2 &&
            (noPermissionsAssigned || hasCreateVehicleTrackingPermission)) ||
          (item.id === 6 &&
            (noPermissionsAssigned ||
              hasPermission(ACTIONS.REPORT.VEHICLE_TRACKING) ||
              hasPermission(ACTIONS.REPORT.VARIANCE) ||
              hasPermission(ACTIONS.REPORT.INWARD) ||
              hasPermission(ACTIONS.REPORT.GRN))) ||
          (item.id === 12 &&
            (noPermissionsAssigned || hasPermission(ACTIONS.REPORT.OUTWARD))),
      );
    } else {
      return null;
    }

    if (noPermissionsAssigned) return roleFiltered;

    return roleFiltered
      .map((item) => {
        if (item.groupChild) {
          return {
            ...item,
            groupChild: item.groupChild.filter(
              (child) => !child.action || hasPermission(child.action),
            ),
          };
        }
        return item;
      })
      .filter((item) => {
        // Filter out items without permission
        if (item.action && !hasPermission(item.action)) {
          return false;
        }
        // Filter out parent items with no visible children
        if (item.groupChild && item.groupChild.length === 0) {
          return false;
        }
        return true;
      });
  };

  return filteredSidebarData;
};

export default SidebarData;
